import { Feature, LineString, MultiLineString, Position } from "geojson";

import { length as turfLength, lineString as turfLineString } from "fond/turf";

export type NodeId = string;
export type EdgeId = string;

export interface UgPathEdge {
  a: NodeId;
  b: NodeId;
  length: number;
  featureId: string | number;
  coordinates: Position[];
}

export interface UgPathGraph {
  nodes: Map<NodeId, [number, number]>;
  edges: Map<EdgeId, UgPathEdge>;
  adjacency: Map<NodeId, Array<{ neighbour: NodeId; edgeId: EdgeId }>>;
}

export interface ShortestPathResult {
  edgeIds: EdgeId[];
  totalLength: number;
}

/**
 * Default merge tolerance: any two endpoints within 1 metre collapse to the same graph node.
 * Underground infrastructure rarely has truly-distinct features closer than a metre, and source
 * data often has sub-metre GIS jitter between endpoints that are meant to coincide. If the
 * shortest-path preview flips as the mouse moves near an endpoint, bump this value.
 */
const DEFAULT_MERGE_TOLERANCE_M = 1.0;

// Approximate metres per degree of latitude (and equatorial longitude). We use this only to
// size a spatial-hash bucket — exact distance checks use haversine.
const METRES_PER_DEGREE = 111_000;

/**
 * Great-circle distance between two lng/lat points in metres. Plenty accurate for a node-merge
 * tolerance test at the sub-metre scale.
 */
function haversineMetres(lng1: number, lat1: number, lng2: number, lat2: number): number {
  const R = 6_371_000;
  const toRad = (d: number): number => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/**
 * Spatial hash that assigns every incoming endpoint to an existing node if one lies within the
 * merge tolerance, otherwise creates a new node. Uses a lat/lng grid whose cell size equals the
 * tolerance; when checking a new endpoint we only consider the 3×3 neighbourhood, so lookup is
 * O(1) amortised regardless of graph size.
 *
 * Node IDs are synthetic (`n0`, `n1`, …). The representative coord is the first endpoint seen
 * for the cluster, which is good enough — the pixel-space snap marker is drawn from this coord,
 * but the actual ug_path geometries passing through the node are untouched.
 */
class NodeMerger {
  private cellSizeDeg: number;

  private cells: Map<string, NodeId[]> = new Map();

  private coords: Map<NodeId, [number, number]> = new Map();

  private nextId = 0;

  constructor(private toleranceM: number) {
    this.cellSizeDeg = toleranceM / METRES_PER_DEGREE;
  }

  private cellKey(cx: number, cy: number): string {
    return `${cx},${cy}`;
  }

  getOrCreate(coord: Position): NodeId {
    const lng = coord[0];
    const lat = coord[1];
    const cx = Math.floor(lng / this.cellSizeDeg);
    const cy = Math.floor(lat / this.cellSizeDeg);

    let bestId: NodeId | null = null;
    let bestDist = Infinity;
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        const bucket = this.cells.get(this.cellKey(cx + dx, cy + dy));
        if (!bucket) continue;
        for (const nodeId of bucket) {
          const [nlng, nlat] = this.coords.get(nodeId)!;
          const distM = haversineMetres(lng, lat, nlng, nlat);
          if (distM <= this.toleranceM && distM < bestDist) {
            bestDist = distM;
            bestId = nodeId;
          }
        }
      }
    }

    if (bestId !== null) return bestId;

    const newId: NodeId = `n${this.nextId}`;
    this.nextId += 1;
    this.coords.set(newId, [lng, lat]);
    const key = this.cellKey(cx, cy);
    const bucket = this.cells.get(key);
    if (bucket) bucket.push(newId);
    else this.cells.set(key, [newId]);
    return newId;
  }

  nodesMap(): Map<NodeId, [number, number]> {
    return this.coords;
  }
}

/**
 * Builds an undirected weighted graph from a ug_path feature collection. Nodes are the
 * unique endpoints of each LineString, with endpoints within `mergeToleranceM` metres of an
 * already-seen endpoint collapsed into the same node. Edges are the ug_paths themselves with
 * weight = turf.length.
 *
 * Features without a stable id (neither feature.id nor feature.properties.id) are skipped —
 * shortest-path mode needs to commit footprint features with `sourceUgPathId`, so
 * un-referenceable edges can't contribute.
 *
 * MultiLineStrings are treated as multiple edges (one per LineString component) sharing the
 * same underlying featureId. This rarely occurs for ug_path in practice but avoids silently
 * dropping data.
 */
export function buildUgPathGraph(features: Array<Feature<LineString | MultiLineString>>, options: { mergeToleranceM?: number } = {}): UgPathGraph {
  const merger = new NodeMerger(options.mergeToleranceM ?? DEFAULT_MERGE_TOLERANCE_M);
  const edges = new Map<EdgeId, UgPathEdge>();
  const adjacency = new Map<NodeId, Array<{ neighbour: NodeId; edgeId: EdgeId }>>();

  const addEdge = (coords: Position[], featureId: string | number, suffix: string): void => {
    if (coords.length < 2) return;
    const a = merger.getOrCreate(coords[0]);
    const b = merger.getOrCreate(coords[coords.length - 1]);

    const edgeId: EdgeId = `${featureId}${suffix}`;
    const length = turfLength(turfLineString(coords), { units: "kilometers" });
    edges.set(edgeId, { a, b, length, featureId, coordinates: coords });

    if (!adjacency.has(a)) adjacency.set(a, []);
    if (!adjacency.has(b)) adjacency.set(b, []);
    adjacency.get(a)!.push({ neighbour: b, edgeId });
    if (a !== b) {
      adjacency.get(b)!.push({ neighbour: a, edgeId });
    }
  };

  for (let i = 0; i < features.length; i += 1) {
    const feature = features[i];
    const featureId = (feature.id ?? feature.properties?.id) as string | number | undefined;
    if (featureId == null) continue;

    if (feature.geometry.type === "LineString") {
      addEdge(feature.geometry.coordinates, featureId, "");
    } else if (feature.geometry.type === "MultiLineString") {
      feature.geometry.coordinates.forEach((part, j) => addEdge(part, featureId, `#${j}`));
    }
  }

  return { nodes: merger.nodesMap(), edges, adjacency };
}

/**
 * Tiny binary-heap priority queue keyed by distance. Supports duplicate entries with lazy
 * deletion — we skip a popped entry if its stored distance is stale. Keeps Dijkstra simple
 * without pulling in a dependency.
 */
class MinHeap {
  private heap: Array<{ nodeId: NodeId; dist: number }> = [];

  push(entry: { nodeId: NodeId; dist: number }): void {
    this.heap.push(entry);
    this.bubbleUp(this.heap.length - 1);
  }

  pop(): { nodeId: NodeId; dist: number } | undefined {
    if (this.heap.length === 0) return undefined;
    const top = this.heap[0];
    const last = this.heap.pop()!;
    if (this.heap.length > 0) {
      this.heap[0] = last;
      this.sinkDown(0);
    }
    return top;
  }

  get size(): number {
    return this.heap.length;
  }

  private bubbleUp(i: number): void {
    while (i > 0) {
      const parent = Math.floor((i - 1) / 2);
      if (this.heap[i].dist < this.heap[parent].dist) {
        [this.heap[i], this.heap[parent]] = [this.heap[parent], this.heap[i]];
        i = parent;
      } else {
        break;
      }
    }
  }

  private sinkDown(i: number): void {
    const n = this.heap.length;
    while (true) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let smallest = i;
      if (l < n && this.heap[l].dist < this.heap[smallest].dist) smallest = l;
      if (r < n && this.heap[r].dist < this.heap[smallest].dist) smallest = r;
      if (smallest === i) break;
      [this.heap[i], this.heap[smallest]] = [this.heap[smallest], this.heap[i]];
      i = smallest;
    }
  }
}

/**
 * Single-source Dijkstra: returns a map of reachable nodeIds to the edge-id path (in order)
 * and total length. Unreachable nodes are absent from the result.
 */
export function dijkstra(graph: UgPathGraph, sourceNodeId: NodeId): Map<NodeId, ShortestPathResult> {
  const dist = new Map<NodeId, number>();
  const prev = new Map<NodeId, { prevNode: NodeId; edgeId: EdgeId }>();
  dist.set(sourceNodeId, 0);

  const heap = new MinHeap();
  heap.push({ nodeId: sourceNodeId, dist: 0 });

  while (heap.size > 0) {
    const { nodeId, dist: d } = heap.pop()!;
    if (d !== dist.get(nodeId)) continue; // stale entry
    const neighbours = graph.adjacency.get(nodeId);
    if (!neighbours) continue;
    for (const { neighbour, edgeId } of neighbours) {
      const edge = graph.edges.get(edgeId);
      if (!edge) continue;
      const nd = d + edge.length;
      if (nd < (dist.get(neighbour) ?? Infinity)) {
        dist.set(neighbour, nd);
        prev.set(neighbour, { prevNode: nodeId, edgeId });
        heap.push({ nodeId: neighbour, dist: nd });
      }
    }
  }

  const result = new Map<NodeId, ShortestPathResult>();
  dist.forEach((totalLength, nodeId) => {
    const edgeIds: EdgeId[] = [];
    let cursor = nodeId;
    while (cursor !== sourceNodeId) {
      const step = prev.get(cursor);
      if (!step) break;
      edgeIds.unshift(step.edgeId);
      cursor = step.prevNode;
    }
    result.set(nodeId, { edgeIds, totalLength });
  });
  return result;
}

/**
 * Finds the graph node closest to the given lng/lat. Returns null if no node is within
 * `maxDegrees` (squared-lng/lat distance, roughly). `maxDegrees` is a crude gate — the caller
 * is expected to do a pixel-distance check for the final UX decision.
 */
export function findNearestNode(
  graph: UgPathGraph,
  lng: number,
  lat: number,
  maxDegrees: number = Infinity
): { nodeId: NodeId; distanceSq: number } | null {
  let bestId: NodeId | null = null;
  let bestSq = maxDegrees * maxDegrees;
  graph.nodes.forEach(([nlng, nlat], nodeId) => {
    const dx = nlng - lng;
    const dy = nlat - lat;
    const sq = dx * dx + dy * dy;
    if (sq < bestSq) {
      bestSq = sq;
      bestId = nodeId;
    }
  });
  return bestId == null ? null : { nodeId: bestId, distanceSq: bestSq };
}

/**
 * Concatenates the edge coordinates along a path into a single continuous LineString
 * coordinate list. Reverses individual edges as needed so the path reads head-to-tail from
 * sourceNode → ... → target.
 */
export function buildPathCoordinates(graph: UgPathGraph, sourceNodeId: NodeId, edgeIds: EdgeId[]): Position[] {
  if (edgeIds.length === 0) return [];
  const out: Position[] = [];
  let cursor = sourceNodeId;
  for (let i = 0; i < edgeIds.length; i += 1) {
    const edge = graph.edges.get(edgeIds[i]);
    if (!edge) return [];
    const coords = edge.a === cursor ? edge.coordinates : [...edge.coordinates].reverse();
    const start = i === 0 ? 0 : 1; // skip duplicate join vertex
    for (let j = start; j < coords.length; j += 1) out.push(coords[j]);
    cursor = edge.a === cursor ? edge.b : edge.a;
  }
  return out;
}
